import { CircularProgress } from '@mui/material';
import React, { FunctionComponent, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Swal from 'sweetalert2';
import { attentionIcon, iconNotifySuccess, iconNotifyAlertTriangle } from '../../../assets/icons/icons';
import { AuthWrapper as Wrapper, Button, FormControl, TextField } from '../../../components/StyledComponents';
import { EMAIL_REGEX } from '../../../models/consts';
import { IError, IRecoveryPassword } from '../../../models/inner-models';
import { recoverPasswordCognito } from '../../../store/actions';
import { useAppDispatch } from '../../../store/hooks';
import { decoratePopUpMessage } from '../../../utils/popUpTextDecorator';
import RecoverPasswordConfirmation from '../RecoverPasswordConfirmation/RecoverPasswordConfirmation';
import '../style.scss';
import { Helmet } from 'react-helmet';
import './style.scss';
import AuthRightAnimation from "../../../components/AuthRightAnimation";
import AuthHeader from "../../../components/Header/AuthHeader";

const ResetPassword: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const { t: translate } = useTranslation();
  const [isCodeSent, setCodeSending] = useState<boolean>(false);
  const [isProcessing, setProcessing] = useState<boolean>(false);
  const navigate = useNavigate();

  const { register, formState: { errors, isValid }, handleSubmit, getValues } = useForm<IRecoveryPassword>({
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<IRecoveryPassword> = async (data: IRecoveryPassword) => {
    try {
      setProcessing(true);
      await dispatch(recoverPasswordCognito(data)).unwrap();
      Swal.fire({
        title: translate('notifications.titles.success'),
        text: 'A verification code has been sent to your email.',
        imageUrl: iconNotifySuccess,
        confirmButtonText: 'OK',
      });
      setCodeSending(true);
    } catch (err) {
      const error = err as IError;

      console.log(err);

      return Swal.fire({
        title: translate('notifications.titles.attention'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: iconNotifyAlertTriangle,
        confirmButtonText: translate('notifications.choices.close'),
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>BackupLABS Reset Password</title>
        <meta name="title" content="BackupLABS Reset Password" />
        <meta name="description" content="Recovery your Password" />
      </Helmet>
      <div className="auth-login auth-login-page">
        <div className="auth-login-left">
          <AuthHeader/>
          <Wrapper className="reset-password auth-new-form">
            <div className="auth-title">
              {translate('forms.recover_password.page_title')}
            </div>
            <div className="notification-text">
              <img src={attentionIcon} loading="lazy" alt="" />
              <div>
                {isCodeSent
                  ? translate('forms.recover_password.enter_new')
                  : translate('forms.recover_password.enter_email')}
              </div>
            </div>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <FormControl className="form-row reset-password__email-row" fullWidth>
                <div className="auth-form__label-row">
                  <div className="label-block">{translate('forms.common.emailA')}</div>
                  {!!errors.email?.message && (
                    <span className="auth-form__field-error">{errors.email.message}</span>
                  )}
                </div>
                <div className="auth-form__input-block">
                  <TextField
                    className="auth-mui-textfield"
                    placeholder={translate('forms.common.email')}
                    required
                    id="email"
                    type="email"
                    disabled={isCodeSent}
                    {...register('email', {
                      required: translate('forms.common.required') as string,
                      pattern: {
                        value: EMAIL_REGEX,
                        message: translate('forms.common.invalid_email'),
                      },
                    })}
                    error={!!errors.email}
                  />
                </div>
              </FormControl>
              {!isCodeSent && (
                <>
                  <div className="action-row">
                    <Button className="primary-button w-100" type="submit" disabled={!isValid || isProcessing}>
                      {isProcessing && (
                        <div className="small-spinner-wrapper">
                          <CircularProgress color="inherit" className="auth-btn-spinner" />
                        </div>
                      )}
                      {isProcessing
                        ? translate('notifications.choices.processing')
                        : translate('forms.recover_password.title')}
                    </Button>
                  </div>
                  <div className="action-row">
                    <Button
                      className="reset-password__cancel-btn"
                      onClick={() => navigate(-1)}
                      variant="outlined"
                      color="primary"
                    >
                      {translate('notifications.choices.cancel')}
                    </Button>
                  </div>
                </>
              )}
            </form>
            {isCodeSent && <RecoverPasswordConfirmation email={getValues('email')} />}
          </Wrapper>
        </div>
        <div className="auth-login-right">
          <AuthRightAnimation/>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
